import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';
import UserMessageBox from '../components/UserMessageBox';
import BootcampUserReflectionsOverview from '../components/bootcamp/BootcampUserReflectionsOverview';
import { getEffectiveIsGlobal } from '../utils/adminViewMode';
import messageIcon from '../assets/message.svg';

function BootcampUserDetail() {
  const { id: bootcampId, userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bootcamp, setBootcamp] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [reflectionCount, setReflectionCount] = useState(0);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');

      let isGlobalLocal = false;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const sessionUserId = sessionData?.session?.user?.id;
        if (sessionUserId) {
          const { data: lean } = await supabase
            .from('lean_admin_users')
            .select('is_global')
            .eq('user_id', sessionUserId)
            .eq('is_global', true)
            .maybeSingle();
          isGlobalLocal = getEffectiveIsGlobal(!!lean);
          setIsGlobalAdmin(isGlobalLocal);
        }
      } catch {
        setIsGlobalAdmin(false);
      }

      const { data: bootcampData, error: bootcampError } = await supabase
        .from('bootcamps')
        .select('id, start_date, end_date, stutter_time, team_id, course_id, courses(name), teams(name, client_id)')
        .eq('id', bootcampId)
        .single();

      if (bootcampError || !bootcampData) {
        setError('Could not load bootcamp.');
        setLoading(false);
        return;
      }

      const rpcName = isGlobalLocal ? 'get_leaderboard' : 'get_leaderboard_manager';
      const [{ data: leaderboardData, error: leaderboardError }, { data: lessonsData, error: lessonsError }, { count, error: reflectionsError }] = await Promise.all([
        supabase.rpc(rpcName, { p_bootcamp_id: Number(bootcampId) }),
        supabase
          .from('lessons')
          .select('lesson_id, position, title, lesson_type')
          .eq('course_id', bootcampData.course_id)
          .order('position', { ascending: true }),
        supabase
          .from('user_reflections')
          .select('*', { count: 'exact', head: true })
          .eq('bootcamp_id', bootcampId)
          .eq('user_id', userId),
      ]);

      if (leaderboardError) {
        setError('Could not load user stats.');
        setLoading(false);
        return;
      }

      if (lessonsError) {
        setError('Could not load bootcamp lessons.');
        setLoading(false);
        return;
      }

      if (reflectionsError) {
        setError('Could not load reflection count.');
        setLoading(false);
        return;
      }

      setBootcamp(bootcampData);
      setLeaderboard(leaderboardData || []);
      setLessons((lessonsData || []).slice().sort((a, b) => {
        const ao = a.lesson_type === 'onboarding' ? 0 : 1;
        const bo = b.lesson_type === 'onboarding' ? 0 : 1;
        if (ao !== bo) return ao - bo;
        return (a.position || 0) - (b.position || 0);
      }));
      setReflectionCount(count || 0);
      setLoading(false);
    };

    fetchData();
  }, [bootcampId, userId]);

  const leaderboardUser = useMemo(() => {
    return (leaderboard || []).find((row) => String(row.id || row.user_id) === String(userId)) || null;
  }, [leaderboard, userId]);

  const teamPosition = useMemo(() => {
    const index = (leaderboard || []).findIndex((row) => String(row.id || row.user_id) === String(userId));
    return index >= 0 ? index + 1 : null;
  }, [leaderboard, userId]);

  const teamName = bootcamp?.teams?.name || 'Unknown team';
  const bootcampName = bootcamp?.courses?.name || 'Bootcamp';
  const displayName = leaderboardUser?.full_name || leaderboardUser?.email || 'User';
  const points = Number(leaderboardUser?.points || 0);
  const teamSize = (leaderboard || []).length;

  const handleBack = () => {
    const params = searchParams.toString();
    navigate({
      pathname: `/bootcamp/${bootcampId}`,
      search: params ? `?${params}` : '',
      state: location.state,
    });
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="modern-loading">Loading user…</div>
      </div>
    );
  }

  if (error || !leaderboardUser) {
    return (
      <div className="dashboard-page">
        <button type="button" className="analytics-back-link" onClick={handleBack}>
          ← Back to bootcamp
        </button>
        <div className="modern-error modern-mt-4">{error || 'User not found in this bootcamp.'}</div>
      </div>
    );
  }

  const messageUser = {
    id: leaderboardUser.id || leaderboardUser.user_id,
    full_name: leaderboardUser.full_name,
    email: leaderboardUser.email,
  };

  return (
    <div className="dashboard-page">
      <RenderMonitor componentName="BootcampUserDetail" />

      <button type="button" className="analytics-back-link" onClick={handleBack}>
        ← Back to bootcamp
      </button>

      <div className="modern-mb-4 bootcamp-user-detail-header">
        <div className="bootcamp-user-detail-title-row">
          {leaderboardUser.icon && (
            <img
              src={leaderboardUser.icon}
              alt=""
              className="bootcamp-user-detail-avatar"
            />
          )}
          <h1 className="modern-title bootcamp-user-detail-name">{displayName}</h1>
          {isGlobalAdmin && (
            <button
              type="button"
              className={`bootcamp-user-send-message-btn${messageOpen ? ' bootcamp-user-send-message-btn--active' : ''}`}
              onClick={() => setMessageOpen((open) => !open)}
              aria-expanded={messageOpen}
            >
              <img src={messageIcon} alt="" aria-hidden="true" className="bootcamp-user-send-message-icon" />
              <span>{messageOpen ? 'Close' : 'Send message'}</span>
            </button>
          )}
        </div>
        {isGlobalAdmin && messageOpen && (
          <div className="bootcamp-user-message-panel">
            <UserMessageBox
              user={messageUser}
              compact
              onClose={() => setMessageOpen(false)}
            />
          </div>
        )}
      </div>

      <div className="analytics-kpi-section">
        <div className="analytics-kpi-grid bootcamp-user-meta-kpi-grid">
          <div className="analytics-kpi-card">
            <div className="analytics-kpi-label">Team</div>
            <div className="analytics-kpi-meta-value">{teamName}</div>
          </div>
          <div className="analytics-kpi-card">
            <div className="analytics-kpi-label">Bootcamp</div>
            <div className="analytics-kpi-meta-value">{bootcampName}</div>
          </div>
        </div>
      </div>

      <div className="analytics-kpi-section">
        <div className="analytics-kpi-grid">
          <div className="analytics-kpi-card analytics-kpi-card--centered">
            <div className="analytics-kpi-value">{points.toLocaleString()}</div>
            <div className="analytics-kpi-label">Points earned</div>
          </div>
          <div className="analytics-kpi-card analytics-kpi-card--centered">
            <div className="analytics-kpi-value">
              {teamPosition != null ? `#${teamPosition}` : '—'}
            </div>
            <div className="analytics-kpi-label">
              Position in team{teamSize > 0 ? ` of ${teamSize}` : ''}
            </div>
          </div>
          <div className="analytics-kpi-card analytics-kpi-card--centered">
            <div className="analytics-kpi-value">{reflectionCount.toLocaleString()}</div>
            <div className="analytics-kpi-label">Reflections written</div>
          </div>
        </div>
      </div>

      <h2 className="modern-heading analytics-section-heading modern-mb-4 modern-mt-6">Reflections</h2>
      <BootcampUserReflectionsOverview
        bootcampId={bootcampId}
        userId={userId}
        lessons={lessons}
      />
    </div>
  );
}

export default BootcampUserDetail;
